import React from 'react';
import { Link } from 'react-router-dom';
import { Shield, Clock, Calendar, ArrowRight, BookOpen, TrendingUp } from 'lucide-react';
import { getAllArticles, getPillarArticle } from '@/data/blogArticles';
import { Helmet } from 'react-helmet-async';
import Header from '@/components/Header';

const BlogPage = () => {
  const allArticles = getAllArticles();
  const pillarArticle = getPillarArticle();
  const otherArticles = allArticles.filter(article => !article.isPillar);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('fr-FR', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  return (
    <>
      <Helmet>
        <title>Blog Anti-Recouvrement | Guides sur la Prescription de Dette</title>
        <meta name="description" content="Guides complets sur la prescription de dette en France. Delais legaux, procedures, conseils pratiques pour URSSAF, banque, CAF, impots et plus." />
        <meta property="og:title" content="Blog Anti-Recouvrement | Guides sur la Prescription de Dette" />
        <meta property="og:description" content="Guides complets sur la prescription de dette en France. Delais legaux, procedures, conseils pratiques." />
        <meta property="og:type" content="website" />
        <link rel="canonical" href="https://www.anti-recouvrement.com/blog" />
      </Helmet>

      <div className="min-h-screen" style={{ backgroundColor: '#f8fafc' }}>
        <Header showAnalyzeCta />

        {/* Hero Section */}
        <section className="py-12 px-4" style={{ backgroundColor: '#1e3a8a' }}>
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-2 mb-4">
              <BookOpen size={28} color="white" />
              <span className="text-white/80 font-medium">Guides & Ressources</span>
            </div>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">
              Tout savoir sur la prescription de dette
            </h1>
            <p className="text-xl text-white/90 max-w-2xl mx-auto">
              Des guides pratiques et complets pour comprendre vos droits face aux creanciers et aux societes de recouvrement.
            </p>
          </div>
        </section>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
          {/* Pillar Article - Featured */}
          {pillarArticle && (
            <section className="mb-12">
              <div className="flex items-center gap-2 mb-6">
                <TrendingUp size={24} color="#1e3a8a" />
                <h2 className="text-2xl font-bold" style={{ color: '#1e3a8a' }}>
                  Guide Principal
                </h2>
              </div>
              <Link to={`/blog/${pillarArticle.slug}`}>
                <div className="card p-8 hover:shadow-xl transition-all duration-300 border-2 border-blue-100 hover:border-blue-300">
                  <div className="flex flex-col md:flex-row gap-6">
                    <div className="flex-1">
                      <span className="inline-block px-3 py-1 text-sm font-medium rounded-full bg-blue-100 text-blue-800 mb-4">
                        {pillarArticle.category}
                      </span>
                      <h3 className="text-2xl md:text-3xl font-bold text-gray-900 mb-4 hover:text-blue-800 transition-colors">
                        {pillarArticle.title}
                      </h3>
                      <p className="text-gray-600 text-lg mb-4">
                        {pillarArticle.excerpt}
                      </p>
                      <div className="flex items-center gap-4 text-sm text-gray-500 mb-4">
                        <span className="flex items-center gap-1">
                          <Clock size={16} />
                          {pillarArticle.readTime} min de lecture
                        </span>
                        <span className="flex items-center gap-1">
                          <Calendar size={16} />
                          {formatDate(pillarArticle.publishedAt)}
                        </span>
                      </div>
                      <span className="inline-flex items-center gap-2 font-semibold" style={{ color: '#1e3a8a' }}>
                        Lire le guide complet
                        <ArrowRight size={18} />
                      </span>
                    </div>
                    <div className="md:w-64 flex items-center justify-center">
                      <div className="w-32 h-32 rounded-full flex items-center justify-center" style={{ backgroundColor: '#1e3a8a' }}>
                        <BookOpen size={48} color="white" />
                      </div>
                    </div>
                  </div>
                </div>
              </Link>
            </section>
          )}

          {/* Other Articles Grid */}
          <section>
            <h2 className="text-2xl font-bold mb-6" style={{ color: '#1e3a8a' }}>
              Guides par type de dette
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {otherArticles.map((article) => (
                <Link key={article.id} to={`/blog/${article.slug}`}>
                  <article className="card p-6 h-full hover:shadow-lg transition-all duration-300 hover:-translate-y-1">
                    <span className="inline-block px-3 py-1 text-xs font-medium rounded-full bg-gray-100 text-gray-700 mb-3">
                      {article.category}
                    </span>
                    <h3 className="text-lg font-bold text-gray-900 mb-2 line-clamp-2 hover:text-blue-800 transition-colors">
                      {article.title}
                    </h3>
                    <p className="text-gray-600 text-sm mb-4 line-clamp-3">
                      {article.excerpt}
                    </p>
                    <div className="flex items-center justify-between text-sm text-gray-500 mt-auto">
                      <span className="flex items-center gap-1">
                        <Clock size={14} />
                        {article.readTime} min
                      </span>
                      <span className="flex items-center gap-1 font-medium" style={{ color: '#1e3a8a' }}>
                        Lire
                        <ArrowRight size={14} />
                      </span>
                    </div>
                  </article>
                </Link>
              ))}
            </div>
          </section>

          {/* CTA Section */}
          <section className="mt-16 card p-8 text-center" style={{ backgroundColor: '#1e3a8a' }}>
            <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">
              Votre dette est peut-etre prescrite
            </h2>
            <p className="text-white/90 text-lg mb-6 max-w-2xl mx-auto">
              Analysez gratuitement votre courrier de recouvrement en quelques minutes et decouvrez si vous etes encore tenu de payer.
            </p>
            <Link
              to="/"
              className="inline-block px-8 py-3 bg-white rounded-lg font-semibold text-lg transition-transform hover:scale-105"
              style={{ color: '#1e3a8a' }}
            >
              Analyser ma dette gratuitement
            </Link>
          </section>
        </div>

        {/* Footer */}
        <footer className="bg-gray-900 text-white py-8">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
              <div className="flex items-center gap-2">
                <Shield size={24} />
                <span className="font-bold">Anti-Recouvrement</span>
              </div>
              <nav className="flex items-center gap-6 text-sm text-gray-400">
                <Link to="/" className="hover:text-white transition-colors">Accueil</Link>
                <Link to="/blog" className="hover:text-white transition-colors">Blog</Link>
                <Link to="/login" className="hover:text-white transition-colors">Connexion</Link>
              </nav>
              <p className="text-sm text-gray-400">
                2025 Anti-Recouvrement. Tous droits reserves.
              </p>
            </div>
          </div>
        </footer>
      </div>
    </>
  );
};

export default BlogPage;
